// The one analytics event /pay/return records once the status route
// (GET /api/stripe/embedded-checkout/status) has told it how a payment ended,
// and the one place its name and properties are spelled: the page builds it
// here, and the nightly replay report (web/lib/replay-report/run.ts) reads it
// under the same names to tell a buyer who paid from one who was declined or
// is still processing.
//
// Analytics only. Nothing reads this event to fulfil, grant or charge
// anything: fulfilment happens in the Stripe webhook on a real
// payment_intent.succeeded (CA-PAYMENT-FULFILL-ONLY-PAID), and a visitor's
// browser can send any event it likes, so it is never proof of payment.
//
// Imported by a client component and by server code alike, so it carries no
// "use client" and no "server-only".

export const PAYMENT_RETURN_EVENT = "payment_return_viewed"

/** The status route's own states, the ones the page shows: paid, still
 * processing, or did not go through (Stripe's requires_payment_method,
 * requires_action, requires_confirmation and canceled). */
export const PAYMENT_RETURN_STATES = ["succeeded", "processing", "failed"] as const
export type PaymentReturnState = (typeof PAYMENT_RETURN_STATES)[number]

/** A PaymentIntent took money; a SetupIntent only saved his card for a weekly
 * plan that charges nothing today. */
export const PAYMENT_RETURN_KINDS = ["payment", "setup"] as const
export type PaymentReturnKind = (typeof PAYMENT_RETURN_KINDS)[number]

/** The property names: the keys of the event below, and what a reader
 * selects (the replay report's HogQL). */
export const PAYMENT_RETURN_PROPS = { state: "payment_state", kind: "intent_kind", plan: "plan_id" } as const

export type PaymentReturnEventProps = {
  [PAYMENT_RETURN_PROPS.state]: PaymentReturnState
  [PAYMENT_RETURN_PROPS.kind]: PaymentReturnKind
  /** The offer, as the plan id the catalog knows; absent for a custom amount. */
  [PAYMENT_RETURN_PROPS.plan]?: string
}

export function isPaymentReturnState(value: unknown): value is PaymentReturnState {
  return typeof value === "string" && (PAYMENT_RETURN_STATES as readonly string[]).includes(value)
}

export function isPaymentReturnKind(value: unknown): value is PaymentReturnKind {
  return typeof value === "string" && (PAYMENT_RETURN_KINDS as readonly string[]).includes(value)
}

/** The event's properties from the status route's answer: its state, whether
 * the intent was a payment or a saved card, and the offer. Never the buyer's
 * email, the client secret, the intent id, an amount or a price, all of which
 * the answer or the page's URL carry. Null when the answer names no state the
 * page shows (a refused or failed lookup), so nothing is recorded. */
export function paymentReturnEventProps(answer: unknown, setup: boolean): PaymentReturnEventProps | null {
  if (!answer || typeof answer !== "object") return null
  const { state, planId } = answer as { state?: unknown; planId?: unknown }
  if (!isPaymentReturnState(state)) return null
  return {
    [PAYMENT_RETURN_PROPS.state]: state,
    [PAYMENT_RETURN_PROPS.kind]: setup ? "setup" : "payment",
    ...(typeof planId === "string" && planId ? { [PAYMENT_RETURN_PROPS.plan]: planId } : {}),
  }
}
